import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  GoogleAuthRequest,
  GoogleAuthResponse,
  GoogleAuthService,
} from '../services/google-auth.service';

interface ConsoleLog {
  time: Date;
  type: 'info' | 'success' | 'error' | 'warn';
  message: string;
}

interface TokenPayload {
  iss: string;
  aud: string;
  sub: string;
  email: string;
  email_verified: boolean;
  name: string;
  picture?: string;
  given_name?: string;
  family_name?: string;
  iat: number;
  exp: number;
}

declare const google: any;

@Component({
  selector: 'app-login-test-google',
  standalone: true,
  imports: [CommonModule, DatePipe],
  templateUrl: './login-test-google.component.html',
  styleUrls: ['./login-test-google.component.scss'],
})
export class LoginTestGoogleComponent implements OnInit, OnDestroy {
  // Configuration
  googleClientId = environment.google.ClientId;
  apiUrl = environment.apiUrls.authApiUrl;
  isDevelopment = environment.apiUrls.baseUrl;

  // State
  showGoogleButton = false;
  isLoading = false;
  lastIdToken = '';
  tokenPayload: TokenPayload | null = null;
  lastApiResponse: GoogleAuthResponse | null = null;
  consoleLogs: ConsoleLog[] = [];

  constructor(
    private googleAuthService: GoogleAuthService,
    private http: HttpClient
  ) {
    this.addLog('info', 'LoginTestGoogleComponent başlatıldı');
  }

  ngOnInit(): void {
    this.addLog('info', 'Component initialize ediliyor...');
    this.initializeGoogleSignIn();
  }

  ngOnDestroy(): void {
    this.addLog('info', 'Component destroy ediliyor...');
  }

  // #region Google OAuth Methods

  /**
   * Google Sign-In'i başlat
   */
  private async initializeGoogleSignIn(): Promise<void> {
    try {
      this.addLog(
        'info',
        `Google Sign-In başlatılıyor... Client ID: ${this.googleClientId.substring(
          0,
          20
        )}...`
      );

      await this.googleAuthService.initializeGoogleSignIn(this.googleClientId);

      // Google button'unu render et
      setTimeout(() => {
        try {
          this.googleAuthService.renderGoogleSignInButton(
            'google-signin-button'
          );
          this.showGoogleButton = true;
          this.addLog(
            'success',
            'Google Sign-In button başarıyla render edildi'
          );
        } catch (error) {
          this.addLog('error', `Button render hatası: ${error as string}`);
          this.showGoogleButton = false;
        }
      }, 1500);
    } catch (error) {
      this.addLog('error', `Google Sign-In başlatılamadı: ${error as string}`);
      this.showGoogleButton = false;
    }
  }

  /**
   * Manuel Google Sign-In
   */
  signInWithGoogle(): void {
    this.addLog('info', 'Manuel Google Sign-In tetiklendi');
    this.isLoading = true;

    try {
      this.googleAuthService.signInWithGoogle(this.googleClientId);

      // Loading state'i 5 saniye sonra kaldır
      setTimeout(() => {
        this.isLoading = false;
      }, 5000);
    } catch (error) {
      this.addLog('error', `Manuel sign-in hatası: ${error as string}`);
      this.isLoading = false;
    }
  }

  /**
   * Google One Tap'i göster
   */
  showOneTap(): void {
    this.addLog('info', 'Google One Tap gösteriliyor...');
    try {
      this.googleAuthService.showGoogleOneTap();
      this.addLog('success', 'Google One Tap başarıyla tetiklendi');
    } catch (error) {
      this.addLog('error', `One Tap hatası: ${error as string}`);
    }
  }

  // #endregion

  // #region Test Methods

  /**
   * Google API durumunu kontrol et
   */
  checkGoogleAPI(): void {
    this.addLog('info', 'Google API durumu kontrol ediliyor...');

    if (typeof window !== 'undefined' && (window as any).google) {
      const googleObj = (window as any).google;
      this.addLog(
        'success',
        `Google API yüklü! Versiyon: ${
          googleObj.accounts?.id ? 'GSI' : 'Legacy'
        }`
      );

      // Google API özelliklerini listele
      if (googleObj.accounts?.id) {
        this.addLog('info', 'Google Identity Services (GSI) mevcut');
        this.addLog(
          'info',
          `Mevcut metodlar: ${Object.keys(googleObj.accounts.id).join(', ')}`
        );
      }

      alert(
        '✅ Google API başarıyla yüklenmiş!\n\nDetaylar için console loglarını kontrol edin.'
      );
    } else {
      this.addLog('error', 'Google API yüklü değil');
      alert(
        '❌ Google API yüklenmemiş!\n\nSayfa yenilemeyi deneyin veya internet bağlantınızı kontrol edin.'
      );
    }
  }

  /**
   * Test ID Token'ı ile manuel test
   */
  testWithSampleToken(): void {
    this.addLog('warn', '⚠️ UYARI: Bu sadece test amaçlı sample token!');

    // Sample JWT token (geçersiz signature ile)
    const sampleToken = this.generateSampleJWT();

    this.addLog('info', 'Sample token ile API test ediliyor...');
    this.testGoogleToken(sampleToken);
  }

  /**
   * Google ID Token'ı test et
   */
  private testGoogleToken(idToken: string): void {
    this.addLog(
      'info',
      `Google ID Token test ediliyor... (${idToken.length} karakter)`
    );
    this.lastIdToken = idToken;

    // Token'ı decode et
    try {
      this.tokenPayload = this.decodeJWT(idToken);
      this.addLog(
        'success',
        `Token başarıyla decode edildi. Email: ${this.tokenPayload?.email}`
      );
    } catch (error) {
      this.addLog('error', `Token decode hatası: ${error as string}`);
    }

    const authRequest: GoogleAuthRequest = {
      idToken,
      customerId: '72c54b1a-8e1c-45ea-8edd-b5da1091e325', // Default customer ID
    };

    this.addLog('info', `API'ye istek gönderiliyor: ${this.apiUrl}/GoogleAuth`);

    this.googleAuthService.authenticateWithGoogle(authRequest).subscribe({
      next: (response) => {
        this.lastApiResponse = response;
        this.addLog(
          'success',
          `Google OAuth API başarılı! Kullanıcı: ${response.name} (${response.email})`
        );
        this.addLog(
          'info',
          `Yeni kullanıcı: ${response.isNewUser ? 'Evet' : 'Hayır'}`
        );

        alert(
          `🎉 Google OAuth Başarılı!\n\n👤 Kullanıcı: ${
            response.name
          }\n📧 Email: ${response.email}\n🆕 Yeni Kullanıcı: ${
            response.isNewUser ? 'Evet' : 'Hayır'
          }\n💬 Mesaj: ${response.message}`
        );
      },
      error: (error) => {
        this.lastApiResponse = null;
        this.addLog(
          'error',
          `Google OAuth API hatası: ${JSON.stringify(error)}`
        );

        let errorMessage = 'Bilinmeyen hata';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        alert(
          `❌ Google OAuth API Hatası!\n\n🔍 Hata: ${errorMessage}\n\n💡 Kontrol Edilecekler:\n• Backend API çalışıyor mu?\n• Google Client ID doğru mu?\n• Network bağlantısı var mı?`
        );
      },
    });
  }

  // #endregion

  // #region Utility Methods

  /**
   * Console log ekle
   */
  private addLog(type: ConsoleLog['type'], message: string): void {
    const log: ConsoleLog = {
      time: new Date(),
      type,
      message,
    };

    this.consoleLogs.unshift(log); // En yenileri üstte göster

    // Son 50 log'u tut
    if (this.consoleLogs.length > 50) {
      this.consoleLogs = this.consoleLogs.slice(0, 50);
    }

    // Gerçek console'a da yaz
    switch (type) {
      case 'info':
        console.log(`[GoogleTest] ${message}`);
        break;
      case 'success':
        console.log(`[GoogleTest] ✅ ${message}`);
        break;
      case 'error':
        console.error(`[GoogleTest] ❌ ${message}`);
        break;
      case 'warn':
        console.warn(`[GoogleTest] ⚠️ ${message}`);
        break;
    }
  }

  /**
   * Logları temizle
   */
  clearLogs(): void {
    this.consoleLogs = [];
    this.addLog('info', 'Console logları temizlendi');
  }

  /**
   * Token'ı kopyala
   */
  copyToken(): void {
    if (this.lastIdToken) {
      navigator.clipboard
        .writeText(this.lastIdToken)
        .then(() => {
          this.addLog('success', "Token clipboard'a kopyalandı");
          alert("📋 Token clipboard'a kopyalandı!");
        })
        .catch((err) => {
          this.addLog('error', `Token kopyalama hatası: ${err as string}`);
          alert('❌ Token kopyalanamadı!');
        });
    }
  }

  /**
   * JWT token'ı decode et
   */
  private decodeJWT(token: string): TokenPayload {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Geçersiz JWT formatı');
      }

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as TokenPayload;
    } catch (error) {
      throw new Error(`JWT decode hatası: ${error as string}`);
    }
  }

  /**
   * Sample JWT oluştur (test için)
   */
  private generateSampleJWT(): string {
    const header = {
      alg: 'RS256',
      kid: 'sample-key-id',
      typ: 'JWT',
    };

    const payload = {
      iss: 'accounts.google.com',
      aud: this.googleClientId,
      sub: '123456789012345678901',
      email: 'test@gmail.com',
      email_verified: true,
      name: 'Test User',
      picture: 'https://lh3.googleusercontent.com/a/default-user',
      given_name: 'Test',
      family_name: 'User',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600,
    };

    const encodedHeader = btoa(JSON.stringify(header))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
    const encodedPayload = btoa(JSON.stringify(payload))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
    const signature = 'sample-signature-for-testing-only';

    return `${encodedHeader}.${encodedPayload}.${signature}`;
  }

  /**
   * TrackBy function for ngFor
   */
  trackByIndex(index: number, item: ConsoleLog): number {
    return index;
  }

  // #endregion

  // #region Google Callback Handler

  /**
   * Google credential response'unu handle et
   * Bu method GoogleAuthService tarafından çağrılacak
   */
  handleGoogleCredentialResponse(response: any): void {
    const idToken = response.credential;
    this.addLog(
      'success',
      `Google credential alındı! Token uzunluğu: ${idToken.length}`
    );

    // Gerçek token ile test et
    this.testGoogleToken(idToken);
  }

  // #endregion
}
